import { LeakRemoveTwoTone } from '@mui/icons-material';
import {
  ReportDto,
  FilterMetricDto,
  ColumnMetricDto,
  ReportFilterGroupDto,
  ReportFilterGroupLinkDto,
  FilterOperatorsDto,
  LogicalOperatorDto,
  ReportFilterGroupFilterDto,
} from '../../../app/services/api/generated';

type DatasetType = 'TRANSACTIONS' | 'CREDIT_REQUEST' | 'ITEMS' | 'THEORY_ACTUAL' | 'STOCK_ON_HAND' | 'WASTE_PERCENTAGE';

interface DatasetDefinition {
  name: string;
  keyFilterMetrics: string[];
  keyColumnMetrics: string[];
  incompatibleWith?: DatasetType[];
}

interface ValidationResult {
  isValid: boolean;
  message: string;
  conflicts?: string[];
  isFilterValid?: boolean;
}

const DATASETS: Record<DatasetType, DatasetDefinition> = {
  TRANSACTIONS: {
    name: 'Transactions',
    keyFilterMetrics: [
      'Transaction Type',
      'Transaction Reason',
      'Transaction Source',
      'Store Number',
      'Date',
      'Start Date',
      'End Date',
      'Source ID',
      'Receipt (Purchase Order) Number',
    ],
    keyColumnMetrics: [
      'Item Number (Supplier Number)',
      'Transaction Type',
      'Transaction Quantity',
      'Cost Price',
      'Store Number',
      'Transaction Reason',
      'Item Description',
      'Date',
      'Transaction Source',
      'Gross Profit',
      'Sales Price',
      'Start Date',
      'End Date',
      'Receipt (Purchase Order) Number',
      'Product Category',
      'Starbucks SKU',
      'Store Name',
      'Shippable UOM',
      'UOM',
      'Made in Store',
      'Source ID',
    ],
    incompatibleWith: ['CREDIT_REQUEST', 'THEORY_ACTUAL', 'STOCK_ON_HAND','WASTE_PERCENTAGE'],
  },
  CREDIT_REQUEST: {
    name: 'Credit Request',
    keyFilterMetrics: [
      'Credit Request Number',
      'Return To Vendor Reason',
      'Credit Requests Value',
      'Credit Requests Quantity',
      'Return To Vendor Quantity',
      'Return To Vendor Cost',
      'Return To Vendor Value',
      'Supplier Reference',
      'Credit Request Reason',
      'Return to Vendor Number',
      'Order Number',
      'Order Quantity',
      'Order Type',
      'Start Date',
      'End Date',
      'Date',
      'Ship Confirmed Quantity',
      'Actual Received Quantity',
    ],
    keyColumnMetrics: [
      'Item Number (Supplier Number)',
      'Store Number',
      'Store Name',
      'Item Description',
      'Product Category',
      'Credit Requests Quantity',
      'Credit Requests Value',
      'Credit Request Reason',
      'Credited UOM',
      'Return To Vendor Quantity',
      'Return To Vendor Cost',
      'Return To Vendor Value',
      'Return To Vendor UOM',
      'Credit Request Number',
      'Order Quantity',
      'Delivery Date',
      'Order Number',
      'Order Type',
      'Start Date',
      'End Date',
      'Date',
      'Cost Price',
      'Starbucks SKU',
      'Shippable UOM',
      'UOM',
      'Return to Vendor Number',
      'Return To Vendor Reason',
      'Supplier Order ID',
      'Supplier Reference',
      'Ship Confirmed Quantity',
      'Actual Received Quantity',
      'Made in Store',
    ],
    incompatibleWith: ['TRANSACTIONS', 'THEORY_ACTUAL', 'WASTE_PERCENTAGE'],
  },
  ITEMS: {
    name: 'Items',
    keyFilterMetrics: [
      'Recent Activity',
      'Item Number',
      'Store Number',
      'Product Category',
      'Starbucks SKU',
      'Store Name',
      'Item Description',
      'Start Date',
      'End Date',
      'Date',
      'Cost Price',
      'Shippable UOM',
      'UOM',
      'Made in Store',
    ],
    keyColumnMetrics: [
      'Recent Activity',
      'Item Number',
      'Store Number',
      'Product Category',
      'Starbucks SKU',
      'Store Name',
      'Item Description',
      'Start Date',
      'End Date',
      'Date',
      'Cost Price',
      'Shippable UOM',
      'UOM',
      'Made in Store',
    ],
    incompatibleWith: [],
  },
  THEORY_ACTUAL: {
    name: 'Theory Actual Report',
    keyFilterMetrics: [
      'Planned Count Date',
      'Made In Store',
      'Start Date',
      'End Date',
      'Date',
    ],
    keyColumnMetrics: [
      'Item Number',
      'Starbucks SKU',
      'Store Number',
      'Store Name',
      'Item Description',
      'Product Category',
      'Start Date',
      'End Date',
      'Closing Stock on Hand',
      'Opening Stock on Hand',
      'Date',
      'Cost Price',
      'Planned Count Date',
      'Shippable UOM',
      'UOM',
      'Last Approved Stock on Hand',
      'Stock on Hand',
      'Theoretical Usage Quantity',
      'Actual Usage Quantity',
      'Variance - Cost Price',
      'Variance - Quantity',
      'Made in Store',
    ],
    incompatibleWith: ['TRANSACTIONS', 'CREDIT_REQUEST', 'WASTE_PERCENTAGE'],
  },
  STOCK_ON_HAND: {
    name: 'Stock on Hand',
    keyFilterMetrics: [
      'Stock Count Date',
      'Stock on Hand', 
      'Start Date',
      'End Date',
      'Date',
    ],
    keyColumnMetrics: [
      'Item Number',
      'Starbucks SKU',
      'Store Number',
      'Store Name',
      'Item Description',
      'Product Category',
      'Last Approved Stock on Hand',
      'Cost Price',
      'Planned Count Date',
      'Shippable UOM',
      'UOM',
      'Closing Stock on Hand',
      'Opening Stock on Hand',
      'Recent Activity',
      'Stock on Hand',
      'Made in Store',
    ],
    incompatibleWith: ['TRANSACTIONS', 'WASTE_PERCENTAGE'],
  },
  WASTE_PERCENTAGE: {
    name: 'Waste Percentage',
    keyFilterMetrics: [
      'Waste Percentage', 
      'Start Date',
      'End Date',  
      'Date',
    ],
    keyColumnMetrics: [
      'Item Number',
      'Transaction Type',
      'Store Number',
      'Store Name',
      'Starbucks SKU',
      'Item Description',
      'LastTransactionDate',
      'Product Category',
      'Transaction Source',
      'Waste Percentage',
      'Start Date',
      'End Date',
      'Made in Store',
    ],
    incompatibleWith: ['CREDIT_REQUEST', 'THEORY_ACTUAL', 'STOCK_ON_HAND','TRANSACTIONS'],
  },
};
// Metrics that can appear in multiple datasets and shouldn't trigger conflicts
const SHARED_METRICS = [
  'Order Type',
  'Store Number',
  'Store Name', 
  'Item Description',
  'Product Category',
  'Starbucks SKU',
  'Cost Price',
  'UOM',
  'Shippable UOM',
  'Made in Store',
  'Item Number (Supplier Number)',
  'Start Date',
  'End Date',
  'Date',
  'Source ID',
];

const findDatasetForMetric = (metricName: string): DatasetType | undefined => {
  const lower = metricName.toLowerCase();
  
  // Ignore shared metrics that don't define dataset type
  if (SHARED_METRICS.some(shared => lower === shared.toLowerCase())) {
    return undefined;
  }
 
  if (
    DATASETS['ITEMS'].keyFilterMetrics.some(m => lower.includes(m.toLowerCase())) ||
    DATASETS['ITEMS'].keyColumnMetrics.some(m => lower.includes(m.toLowerCase()))
  ) {
    return 'ITEMS';
  }
 
  for (const [type, def] of Object.entries(DATASETS)) {
    if (type === 'ITEMS') continue;  
    if (
      def.keyFilterMetrics.some(m => lower.includes(m.toLowerCase())) ||
      def.keyColumnMetrics.some(m => lower.includes(m.toLowerCase()))
    ) {
      return type as DatasetType;
    }
  }

  return undefined;
}; 

const validateDatasetConsistency = (
  report: ReportDto,
  metricsMap: Map<number, FilterMetricDto>,
  columnMetricsMap: Map<number, ColumnMetricDto>,
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const columnDatasets = new Set<DatasetType>(); 
  const usedColumnMetrics: string[] = [];
 
  (report.reportColumns || []).forEach(column => {
    if (column.metricId) {
      const metric = columnMetricsMap.get(column.metricId);
      if (metric?.name) {
        usedColumnMetrics.push(metric.name);
        const dataset = findDatasetForMetric(metric.name);
        if (dataset) columnDatasets.add(dataset);
      }
    }
  });

  const datasetsArray = Array.from(columnDatasets); 
  for (let i = 0; i < datasetsArray.length; i++) {
    for (let j = i + 1; j < datasetsArray.length; j++) {
      const d1 = datasetsArray[i];
      const d2 = datasetsArray[j];
      if (DATASETS[d1].incompatibleWith?.includes(d2)) {
        errors.push(`Cannot mix ${DATASETS[d1].name} with ${DATASETS[d2].name} metrics`);
      }
      if (DATASETS[d2].incompatibleWith?.includes(d1)) {
        errors.push(`Cannot mix ${DATASETS[d2].name} with ${DATASETS[d1].name} metrics`);
      }
    }
  }

  if (datasetsArray.length === 1) {
    const def = DATASETS[datasetsArray[0]];
    const valid = [...def.keyFilterMetrics, ...def.keyColumnMetrics].map(m => m.toLowerCase());
   
    usedColumnMetrics.forEach(name => {
      if (!valid.some(v => name.toLowerCase().includes(v))) {
        warnings.push(`Column metric "${name}" may not be relevant for ${def.name}`);
      }
    });
  }

  const messages = [...errors, ...warnings];

  const warningMessages = [
    'Some columns will be empty due to mixed datasets. Recommendation:',
    '1. Check your column metrics all come from compatible datasets',
    '2. Review dataset incompatibilities in the documentation',
    '3. Consider splitting this into separate reports for each dataset type',
  ];
  return {
    isValid: errors.length === 0,
    message: messages.length ? 'Some columns will be empty due to mixed datasets': 'Dataset consistency OK',
    conflicts: messages.length ? warningMessages : undefined,
  };
}; 

const hasConflictingFilters = (
  filterGroups: ReportFilterGroupDto[],
  groupLinks: ReportFilterGroupLinkDto[],
  metricsMap: Map<number, FilterMetricDto>,
  operatorsMap: Map<number, FilterOperatorsDto[]>,
  logicalOperators?: LogicalOperatorDto[],
): boolean => {
  const andOperatorId = logicalOperators?.find(op => op.name?.toUpperCase() === 'AND')?.id;
  const groupConflicts = new Map<number, boolean>();

  for (const group of filterGroups) {
    const filters = group.reportFilterGroupFilters ?? [];
 
    const andFilters = filters.filter(f => {
      const logicId = f.logicalOperatorId;
      return logicId == null || logicId === andOperatorId;
    });

    const metricConditions = new Map<number, Map<string, Set<string>>>();

    for (const f of andFilters) {
      const metricId = f.filter?.filterMetricId;
      const opId = f.filter?.operatorId;
      const rawValue = f.filter?.value;
      const value = rawValue?.trim().toLowerCase();

      if (!metricId || !opId || !value) continue;

      const opName = getOperatorName(metricId, opId, operatorsMap);
      if (!opName) continue;

      if (!metricConditions.has(metricId)) metricConditions.set(metricId, new Map());
      const opsMap = metricConditions.get(metricId)!;

      if (!opsMap.has(opName)) opsMap.set(opName, new Set());
      opsMap.get(opName)!.add(value);
    }

    const hasConflict = Array.from(metricConditions.entries()).some(([metricId, opsMap]) => {
      const equals = Array.from(opsMap.get('Equal') ?? []);
      const notEquals = new Set(opsMap.get('Not equal') ?? []);
      const gtValues = Array.from(opsMap.get('Greater than') ?? []).map(v => parseFloat(v)).filter(v => !isNaN(v));
      const ltValues = Array.from(opsMap.get('Less than') ?? []).map(v => parseFloat(v)).filter(v => !isNaN(v));

      if (equals.length > 1 && new Set(equals).size > 1) {
        return true; 
      }

      for (const eqVal of equals) {
        if (notEquals.has(eqVal)) {
          return true; 
        }
      }

      if (gtValues.length && ltValues.length) {
        const maxGt = Math.max(...gtValues);
        const minLt = Math.min(...ltValues);
        if (maxGt >= minLt) {
          return true;  
        }
      }

      return false;
    });

    if (group.id != null) {
      groupConflicts.set(group.id, hasConflict);
    }
  }

  if (filterGroups.length === 1) {
    const groupId = filterGroups[0].id ?? -1;
    return groupConflicts.get(groupId) ?? false;
  }

  return hasGroupConflictFromLinks(
    groupConflicts,
    groupLinks,
    andOperatorId,
    filterGroups,
    operatorsMap,
  );
};
 
const getOperatorName = (
  metricId: number,
  operatorId: number,
  operatorsMap: Map<number, FilterOperatorsDto[]>,
): string => {
  return operatorsMap.get(metricId)?.find(o => o.id === operatorId)?.description ?? '';
};
  
const isEqualsOperator = (metricId: number, opId: number, operatorsMap: Map<number, FilterOperatorsDto[]>) => {
  const ops = operatorsMap.get(metricId) ?? [];
  const op = ops.find(o => o.id === opId);
 
  return op?.description?.toUpperCase() === 'EQUAL'; 
};
 
 
const hasGroupConflictFromLinks = (
  groupConflictStatus: Map<number, boolean>,
  groupLinks: ReportFilterGroupLinkDto[],
  andOperatorId: number | undefined,
  filterGroups: ReportFilterGroupDto[],
  filterOperatorsMap: Map<number, FilterOperatorsDto[]>,
): boolean => {
  const groupGraph = new Map<number, { neighbor: number; isAnd: boolean }[]>();

  for (const link of groupLinks) {
    const g1 = link.group1Id ?? -1;
    const g2 = link.group2Id ?? -1;
    const isAnd = link.logicalOperatorId === andOperatorId;

    if (!groupGraph.has(g1)) groupGraph.set(g1, []);
    if (!groupGraph.has(g2)) groupGraph.set(g2, []);

    groupGraph.get(g1)!.push({ neighbor: g2, isAnd });
    groupGraph.get(g2)!.push({ neighbor: g1, isAnd });
  }

  const visited = new Set<number>();

  const dfs = (
    groupId: number,
    component: Set<number>,
    edges: { from: number; to: number; isAnd: boolean }[],
  ) => {
    if (visited.has(groupId)) return;
    visited.add(groupId);
    component.add(groupId);

    const neighbors = groupGraph.get(groupId) ?? [];
    for (const { neighbor, isAnd } of neighbors) {
      edges.push({ from: groupId, to: neighbor, isAnd });
      dfs(neighbor, component, edges);
    }
  };

  for (const groupId of Array.from(groupConflictStatus.keys())) {
    if (visited.has(groupId)) continue;

    const component = new Set<number>();
    const edges: { from: number; to: number; isAnd: boolean }[] = [];
    dfs(groupId, component, edges);

    for (const { from, to, isAnd } of edges) {
      const fromConflict = groupConflictStatus.get(from) ?? false;
      const toConflict = groupConflictStatus.get(to) ?? false;

      if (isAnd) {
        if (fromConflict || toConflict) {
          return true;
        }

        const filtersA = (filterGroups.find(g => g.id === from)?.reportFilterGroupFilters ?? [])
          .filter(f => f.logicalOperatorId == null || f.logicalOperatorId === andOperatorId);
        const filtersB = (filterGroups.find(g => g.id === to)?.reportFilterGroupFilters ?? [])
          .filter(f => f.logicalOperatorId == null || f.logicalOperatorId === andOperatorId);

        const getEqualValues = (filters: ReportFilterGroupFilterDto[], metricId: number) => {
          return filters
            .filter(f =>
              f.filter?.filterMetricId === metricId &&
              isEqualsOperator(metricId, f.filter.operatorId!, filterOperatorsMap),
            )
            .map(f => f.filter!.value?.trim().toLowerCase())
            .filter(v => !!v) as string[];
        };

        const metricIdsA = filtersA.map(f => f.filter?.filterMetricId).filter(Boolean) as number[];
        const metricIdsB = filtersB.map(f => f.filter?.filterMetricId).filter(Boolean) as number[];

        const sharedMetricIds = metricIdsA.filter(m => metricIdsB.includes(m));

        for (const metricId of sharedMetricIds) {
          const valuesA = new Set(getEqualValues(filtersA, metricId));
          const valuesB = new Set(getEqualValues(filtersB, metricId));

          const intersection = Array.from(valuesA).filter(v => valuesB.has(v));
          if (intersection.length === 0) {
            return true; 
          }
        }
      } else if (fromConflict && toConflict) {
        return true;
      }
    }
  }

  return false;
};

const getValidationMessages = (
  hasDatasetConflict: boolean,
  hasDatasetMismatch: boolean,
  hasGroupConflict: boolean,
  hasFilterConflict: boolean,
): string[] => {
  const messages: string[] = [];  
  if (hasDatasetConflict) {
    messages.push(
      '  Some columns will be empty due to mixed datasets:',
      '   • Ensure selected columns contain compatible data', 
      '   • Consider splitting the report into separate reports for each dataset',
    );
  }
 
  if (hasDatasetMismatch) {
    if (messages.length > 0) messages.push(''); 
    messages.push(
      ' Filter dataset mismatch:',
      '   • Some filters will be ignored',
      '   • Filters and columns use incompatible datasets',
      '   • Verify all filters match your report dataset type',
    );
  }
 
  if (hasFilterConflict) {
    if (messages.length > 0) messages.push(''); 
    messages.push(
      ' The report may be empty due to conflicting filters:',
      '   • If using the AND operator, ensure using different metrics',
      '   • No overlapping values found for shared conditions', 
    );
  } 
  return messages;
};

const validateDatasetMismatch = (
  report: ReportDto,
  metricsMap: Map<number, FilterMetricDto>,
  columnMetricsMap: Map<number, ColumnMetricDto>,
): boolean => {
  const datasetSet = new Set<DatasetType>();
  const usedFields = new Set<string>();

  const isItemsDataset = (dataset: DatasetType) => dataset === 'ITEMS';

  let hasFilter = false;
 
  (report.reportFilterGroups || []).forEach(group => {
    (group.reportFilterGroupFilters || []).forEach(filter => {
      const metric = filter.filter?.filterMetricId ? metricsMap.get(filter.filter.filterMetricId) : undefined;
      if (metric?.name) {
        const dataset = findDatasetForMetric(metric.name);
        if (dataset) {
          datasetSet.add(dataset);
        }
        usedFields.add(metric.name.toLowerCase());
        hasFilter = true;
      }
    });
  });
 
  (report.reportColumns || []).forEach(column => {
    if (column.metricId) {
      const metric = columnMetricsMap.get(column.metricId);
      if (metric?.name) {
        const dataset = findDatasetForMetric(metric.name);
        if (dataset) {
          datasetSet.add(dataset);
        }
        usedFields.add(metric.name.toLowerCase());
      }
    }
  });

  const datasets = Array.from(datasetSet);
  const nonItemsDatasets = datasets.filter(d => !isItemsDataset(d));

  if (!hasFilter) {
    return false;
  }
 
  if (nonItemsDatasets.length > 1) {
    for (let i = 0; i < nonItemsDatasets.length; i++) {
      for (let j = i + 1; j < nonItemsDatasets.length; j++) {
        const d1 = nonItemsDatasets[i];
        const d2 = nonItemsDatasets[j];
        if (DATASETS[d1].incompatibleWith?.includes(d2) || DATASETS[d2].incompatibleWith?.includes(d1)) {
          return true;
        }
      }
    }
  } 
  if (nonItemsDatasets.length === 1) {
    const mainDataset = nonItemsDatasets[0]; 
    const allowedFields = [
      ...(DATASETS[mainDataset].keyFilterMetrics || []),
      ...(DATASETS[mainDataset].keyColumnMetrics || []),
    ].map(f => f.toLowerCase());

    for (const field of Array.from(usedFields)) {
      if (!allowedFields.includes(field)) {
        return true;  
      }
    }
  }

  return false;
};

 
export const validateReport = (
  report: ReportDto,
  metricsMap: Map<number, FilterMetricDto>,
  columnMetricsMap: Map<number, ColumnMetricDto>,
  filterOperatorsMap: Map<number, FilterOperatorsDto[]>,
  logicalOperators?: LogicalOperatorDto[],
  groupLinks: ReportFilterGroupLinkDto[] = [],
): ValidationResult => {
  const datasetResult = validateDatasetConsistency(report, metricsMap, columnMetricsMap);
 
  const hasDatasetMismatch = validateDatasetMismatch(report, metricsMap, columnMetricsMap);

  const groupConflicts = new Map<number, boolean>();
  const hasFilterConflict = hasConflictingFilters(
    report.reportFilterGroups ?? [],
    groupLinks,
    metricsMap, 
    filterOperatorsMap,
    logicalOperators,
  );
 
  const isEqualsOperator = (metricId: number, opId: number, operatorsMap: Map<number, FilterOperatorsDto[]>) => {
    const ops = operatorsMap.get(metricId) ?? [];
    return ops.find(o => o.id === opId)?.description?.toUpperCase() === 'EQUAL';
  };
  const hasGroupConflictFromLinks = (
    groupConflictStatus: Map<number, boolean>,
    groupLinks: ReportFilterGroupLinkDto[],
    andOperatorId: number | undefined,
    filterGroups: ReportFilterGroupDto[],
    filterOperatorsMap: Map<number, FilterOperatorsDto[]>,
  ): boolean => {
    const groupGraph = new Map<number, { neighbor: number; isAnd: boolean }[]>();

    for (const link of groupLinks) {
      const g1 = link.group1Id ?? -1;
      const g2 = link.group2Id ?? -1;
      const isAnd = link.logicalOperatorId === andOperatorId;

      if (!groupGraph.has(g1)) groupGraph.set(g1, []);
      if (!groupGraph.has(g2)) groupGraph.set(g2, []);

    groupGraph.get(g1)!.push({ neighbor: g2, isAnd });
    groupGraph.get(g2)!.push({ neighbor: g1, isAnd });
    }

    const visited = new Set<number>();

    const dfs = (
      groupId: number,
      component: Set<number>,
      edges: { from: number; to: number; isAnd: boolean }[],
    ) => {
      if (visited.has(groupId)) return;
      visited.add(groupId);
      component.add(groupId);

      const neighbors = groupGraph.get(groupId) ?? [];
      for (const { neighbor, isAnd } of neighbors) {
        edges.push({ from: groupId, to: neighbor, isAnd });
        dfs(neighbor, component, edges);
      }
    };

    for (const groupId of Array.from(groupConflictStatus.keys())) {
      if (visited.has(groupId)) continue;

      const component = new Set<number>();
      const edges: { from: number; to: number; isAnd: boolean }[] = [];
      dfs(groupId, component, edges);

      for (const { from, to, isAnd } of edges) {
        const fromConflict = groupConflictStatus.get(from) ?? false;
        const toConflict = groupConflictStatus.get(to) ?? false;

        if (isAnd) {
          const filtersA = filterGroups.find(g => g.id === from)?.reportFilterGroupFilters ?? [];
          const filtersB = filterGroups.find(g => g.id === to)?.reportFilterGroupFilters ?? [];

          const getEqualValues = (filters: ReportFilterGroupFilterDto[], metricId: number) => {
            return filters
              .filter(f =>
                f.filter?.filterMetricId === metricId &&
              isEqualsOperator(metricId, f.filter.operatorId!, filterOperatorsMap),
              )
              .map(f => f.filter!.value?.trim().toLowerCase())
              .filter(v => !!v) as string[];
          };

          const metricIdsA = filtersA.map(f => f.filter?.filterMetricId).filter(Boolean) as number[];
          const metricIdsB = filtersB.map(f => f.filter?.filterMetricId).filter(Boolean) as number[];

          const sharedMetricIds = metricIdsA.filter(m => metricIdsB.includes(m));

          for (const metricId of sharedMetricIds) {
            const valuesA = new Set(getEqualValues(filtersA, metricId));
            const valuesB = new Set(getEqualValues(filtersB, metricId));

            const intersection = Array.from(valuesA).filter(v => valuesB.has(v));
            if (intersection.length === 0) {
              return true;  
            }
          }
        } else if (!fromConflict && !toConflict) continue; 
      }
    }

    return false;
  };

  const andOperatorId = logicalOperators?.find(op => op.name?.toUpperCase() === 'AND')?.id;
  const hasGroupConflict = hasGroupConflictFromLinks(
    groupConflicts,
    groupLinks,
    andOperatorId,
    report.reportFilterGroups ?? [],
    filterOperatorsMap,
  );

  const allMessages = [...(datasetResult.conflicts ?? [])];
 
  const validationMessages = getValidationMessages(!datasetResult.isValid, hasDatasetMismatch,hasGroupConflict, hasFilterConflict);
  const dialogMessage = [ 
    ...validationMessages,
  ].join('\n');
   
 

  return {
    isValid: datasetResult.isValid && !hasFilterConflict && !hasDatasetMismatch && !hasGroupConflict,
    message: dialogMessage ,
    conflicts: allMessages.length ? allMessages : undefined,
    isFilterValid: !hasFilterConflict && !hasGroupConflict,
  };
};
export const getReportDatasetType = (
  report: ReportDto,
  filterMetrics: FilterMetricDto[],
  columnMetrics: ColumnMetricDto[],
): string | null => {
  const usedDatasets = new Set<DatasetType>();

  report.reportColumns?.forEach(column => {
    const metric = columnMetrics.find(m => m.id === column.metricId);
    if (metric?.name) {
      const dataset = findDatasetForMetric(metric.name);
      if (dataset) usedDatasets.add(dataset);
    }
  });

  report.reportFilterGroups?.forEach(group =>
    group.reportFilterGroupFilters?.forEach(filter => {
      const metric = filterMetrics.find(m => m.id === filter.filter?.filterMetricId);
      if (metric?.name) {
        const dataset = findDatasetForMetric(metric.name);
        if (dataset) usedDatasets.add(dataset);
      }
    }),
  );

  const onlyDataset = Array.from(usedDatasets)[0];
  return usedDatasets.size === 1 && onlyDataset ? DATASETS[onlyDataset].name : null;
};

export default {
  validateReport,
  getReportDatasetType,
  DATASETS,
};